import type {
  RenderCountEntry,
  RenderCounterListener,
} from "./render-counter-store.types";

export type { RenderCountEntry } from "./render-counter-store.types";

// Cap the number of tracked component names to prevent unbounded growth
// when many different component names are tracked during a long session.
const MAX_TRACKED_COMPONENTS = 200;

const counts = new Map<string, RenderCountEntry>();
const listeners = new Set<RenderCounterListener>();

let snapshot: RenderCountEntry[] = [];
const SERVER_SNAPSHOT: RenderCountEntry[] = [];
let renderNotificationScheduled = false;

function notify() {
  snapshot = Array.from(counts.values()).sort((a, b) => b.count - a.count);
  listeners.forEach((l) => l());
}

function scheduleRenderNotification() {
  if (renderNotificationScheduled) return;
  renderNotificationScheduled = true;
  queueMicrotask(() => {
    renderNotificationScheduled = false;
    notify();
  });
}

export function subscribeRenderCounterStore(
  listener: RenderCounterListener,
): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getRenderCounts(): RenderCountEntry[] {
  return snapshot;
}

export function getServerSnapshotRenderCounts(): RenderCountEntry[] {
  return SERVER_SNAPSHOT;
}

export function trackRender(name: string, note?: string): void {
  const now = Date.now();
  const existing = counts.get(name);
  if (existing) {
    const deltaMs = now - existing.lastRenderedAt;
    const nextCount = existing.count + 1;
    const historyItem = {
      id: nextCount,
      timestamp: now,
      deltaMs,
      note:
        note ||
        (nextCount === 2 && deltaMs < 100
          ? "StrictMode double-invoke"
          : undefined),
    };
    const newHistory = [historyItem, ...existing.history].slice(0, 30);
    const totalDeltas = newHistory.reduce((acc, h) => acc + h.deltaMs, 0);
    const avgDeltaMs = Math.round(totalDeltas / newHistory.length);
    const isRapid = deltaMs < 50 && nextCount > 2;

    counts.set(name, {
      ...existing,
      count: nextCount,
      lastRenderedAt: now,
      avgDeltaMs,
      status: isRapid ? "rapid" : "stable",
      history: newHistory,
    });
  } else {
    // Prune the least-recently-rendered entry when the cap is reached.
    if (counts.size >= MAX_TRACKED_COMPONENTS) {
      let oldestKey = "";
      let oldestTime = Infinity;
      counts.forEach((entry, key) => {
        if (entry.lastRenderedAt < oldestTime) {
          oldestTime = entry.lastRenderedAt;
          oldestKey = key;
        }
      });
      if (oldestKey) counts.delete(oldestKey);
    }
    counts.set(name, {
      name,
      count: 1,
      firstRenderedAt: now,
      lastRenderedAt: now,
      avgDeltaMs: 0,
      status: "stable",
      history: [
        { id: 1, timestamp: now, deltaMs: 0, note: note || "Initial mount" },
      ],
    });
  }
  // Render trackers run during component render; publish only after that phase.
  scheduleRenderNotification();
}

export function resetRenderCounts(): void {
  counts.clear();
  notify();
}

export function removeRenderTracker(name: string): void {
  counts.delete(name);
  notify();
}
